// src/services/authService.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';

interface LoginRequest {
  email: string;
  password: string;
}

interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  metadata?: any;
}

interface AuthResponse {
  success: boolean;
  data: {
    access_token?: string;
    refresh_token?: string;
    token?: string;
    user: {
      id: string;
      email: string;
      email_confirmed_at?: string;
      confirmed_at?: string;
      email_verified?: boolean;
      role?: string;
      metadata?: any;
    };
    expires_in?: number;
  };
  message: string;
  timestamp?: string;
}

interface VerifyEmailRequest {
  email: string;
  otp: string;
}

interface ApiError {
  success: false;
  error: {
    message: string;
    type: string;
    code?: string;
    timestamp?: string;
  };
}

class AuthService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error?.message || 'Login failed');
      }

      // Store tokens
      if (data.data.access_token) {
        localStorage.setItem('access_token', data.data.access_token);
      }
      if (data.data.refresh_token) {
        localStorage.setItem('refresh_token', data.data.refresh_token);
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Register new user
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error?.message || 'Registration failed');
      }

      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Verify email with OTP code
   */
  async verifyEmail(data: VerifyEmailRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          otp: data.otp,
          code: data.otp,
          token_hash: data.otp
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error?.message || `HTTP error! status: ${response.status}`);
      }

      if (!responseData.success) {
        throw new Error(responseData.error?.message || 'Email verification failed');
      }

      return responseData;
    } catch (error) {
      console.error('Email verification error:', error);
      throw error;
    }
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to resend verification email');
      }

      return data;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<any> {
    try {
      const token = localStorage.getItem('access_token');
      
      if (token) {
        const response = await fetch(`${this.baseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        });

        const data = await response.json();

        if (!response.ok) {
          console.error('Logout error:', data.error?.message);
        }
      }

      // Clear tokens regardless of API response
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Clear tokens even if API call fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return { success: true };
    }
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<any> {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${this.baseUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to get user info');
      }

      return data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  /**
   * Request password reset (Step 1: Send reset email)
   */
  async requestPasswordReset(email: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error?.message || 'Password reset request failed');
      }

      return data;
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  }

  /**
   * Reset password with token (Step 2: Submit new password)
   */
  async resetPassword(accessToken: string, newPassword: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: accessToken,
          new_password: newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const error: any = new Error(data.error?.message || `HTTP error! status: ${response.status}`);
        error.code = data.error?.code;
        throw error;
      }

      if (!data.success) {
        const error: any = new Error(data.error?.message || 'Password reset failed');
        error.code = data.error?.code;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<any> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        throw new Error('No refresh token found');
      }

      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error?.message || 'Token refresh failed');
      }

      // Update tokens
      if (data.data.access_token) {
        localStorage.setItem('access_token', data.data.access_token);
      }
      if (data.data.refresh_token) {
        localStorage.setItem('refresh_token', data.data.refresh_token);
      }

      return data;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  /**
   * Check authentication system health
   */
  async checkAuthHealth(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/health`);
      return await response.json();
    } catch (error) {
      console.error('Auth health check failed:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const authService = new AuthService();

// Utility function to handle API errors
export const handleAuthError = (error: any): string => {
  if (error?.message) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'An unexpected authentication error occurred';
};

// Type guards
export const isAuthResponse = (data: any): data is AuthResponse => {
  return data && typeof data === 'object' && 'success' in data && 'data' in data;
};

export const isApiError = (data: any): data is ApiError => {
  return data && typeof data === 'object' && data.success === false && 'error' in data;
};